(() => {
  'use strict';
  const script = document.currentScript || document.querySelector('script[data-cs-newsletter-policy]');
  if (!script || window.csNewsletterDomainGuard) return;
  window.csNewsletterDomainGuard = true;
  let blockedMessage = script.dataset.blockedMessage;
  let unavailableMessage = script.dataset.unavailableMessage;
  let successMessage = script.dataset.successMessage;
  const messages = script.dataset.csNewsletterLocales ? fetch(script.dataset.csNewsletterLocales, { credentials: 'omit' })
    .then(response => { if (!response.ok) throw new Error('locale_unavailable'); return response.json(); })
    .then(locales => {
      const locale = (script.dataset.language || document.documentElement.lang || 'de').split('-')[0];
      const copy = (locales[locale] || locales.en).cs_newsletter_consent;
      blockedMessage = copy.disposable_email;
      unavailableMessage = copy.validation_unavailable;
      successMessage = copy.confirmation_requested;
    }) : Promise.resolve();
  let policy;
  let load;
  const submitting = new WeakSet();
  function isNewsletterInput(input) {
    if (!(input instanceof HTMLInputElement) || input.type !== 'email') return false;
    const form = input.form;
    if (form?.querySelector('input[name="contact[tags]"][value*="newsletter"]')) return true;
    return Boolean(input.closest('[class*="klaviyo-form"], [data-klaviyo-form]'));
  }
  async function getPolicy() {
    if (policy) return policy;
    if (!load) load = fetch(script.dataset.csNewsletterPolicy, { cache: 'no-cache', credentials: 'omit' })
      .then(response => { if (!response.ok) throw new Error('policy_unavailable'); return response.json(); })
      .then(data => {
        if (data.version !== 1 || !Array.isArray(data.blockedDomains) || data.blockedDomains.length < 1000) throw new Error('invalid_policy');
        policy = { blocked: new Set(data.blockedDomains), allowed: new Set(data.allowedDomains || []) };
        return policy;
      }).catch(error => { load = null; throw error; });
    return load;
  }
  function blocked(email, rules) {
    const pieces = String(email).trim().toLowerCase().split('@');
    if (pieces.length !== 2) return false; // Keep the browser's native syntax validation.
    let domain;
    try { domain = new URL(`https://${pieces[1]}`).hostname.replace(/\.$/, ''); } catch { return false; }
    const parts = domain.split('.');
    for (let i = 0; i < parts.length - 1; i += 1) {
      const candidate = parts.slice(i).join('.');
      if (rules.allowed.has(candidate)) return false;
      if (rules.blocked.has(candidate)) return true;
    }
    return false;
  }
  function apply(input, rules) {
    const rejected = blocked(input.value, rules);
    input.setCustomValidity(rejected ? blockedMessage : '');
    return !rejected;
  }
  document.addEventListener('input', event => {
    if (!isNewsletterInput(event.target)) return;
    event.target.setCustomValidity('');
    if (policy && blockedMessage) apply(event.target, policy);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    const inputs = Array.from(form.querySelectorAll('input[type="email"]')).filter(isNewsletterInput);
    if (!inputs.length) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (submitting.has(form)) return;
    submitting.add(form);
    const submitters = Array.from(form.querySelectorAll('button[type="submit"],input[type="submit"]'));
    submitters.forEach(button => { button.disabled = true; });
    Promise.all([getPolicy(), messages]).then(async ([rules]) => {
      if (!inputs.every(input => apply(input, rules))) { form.reportValidity(); return; }
      const response = await fetch(script.dataset.subscribeEndpoint, { method: 'POST', credentials: 'omit', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: inputs[0].value, locale: document.documentElement.lang || 'de', website: form.querySelector('[data-newsletter-honeypot]')?.value || '' }) });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        inputs[0].setCustomValidity(result.error === 'newsletter_email_disposable' ? blockedMessage : unavailableMessage);
        inputs[0].reportValidity();
        return;
      }
      let success = form.querySelector('[data-newsletter-confirmation]');
      if (!success) {
        success = document.createElement('p');
        success.dataset.newsletterConfirmation = 'true';
        success.setAttribute('role', 'status');
        success.setAttribute('aria-live', 'polite');
        form.append(success);
      }
      success.textContent = successMessage;
      inputs[0].value = '';
    }).catch(() => { inputs[0].setCustomValidity(unavailableMessage); inputs[0].reportValidity(); })
      .finally(() => { submitting.delete(form); submitters.forEach(button => { button.disabled = false; }); });
  }, true);
  // Klaviyo forms use button handlers as well as native form submission.
  document.addEventListener('click', event => {
    const button = event.target.closest?.('button,input[type="submit"]');
    const container = button?.closest('[class*="klaviyo-form"], [data-klaviyo-form]');
    if (!container || !policy || !blockedMessage) return;
    const input = Array.from(container.querySelectorAll('input[type="email"]')).find(isNewsletterInput);
    if (input && !apply(input, policy)) { event.preventDefault(); event.stopImmediatePropagation(); input.reportValidity(); }
  }, true);
  getPolicy().catch(() => {});
  messages.catch(() => {});
})();
